import { useEffect, useMemo, useState } from "react";
import { Icon } from "../icons";
import {
  listLocalBridgeConnections,
  type LocalBridgeConnection,
} from "../localBridgeApi";
import { pilotScenarios } from "../pilotScenarios";
import { PilotSampleChats } from "./PilotSampleChats";
import { getRemotePilotProfile, updateRemotePilotProfile, type PilotProfile } from "../pilotApi";
import type { AuthSession, Conversation, WorkspaceView } from "../types";

type Notify = (tone: "success" | "warning" | "danger", text: string) => void;

type PilotCenterProps = {
  session: AuthSession | null;
  organizationName?: string;
  isAdministrator: boolean;
  remote: boolean;
  onCreateScenario: (conversation: Conversation) => Promise<void> | void;
  onNavigate: (view: WorkspaceView) => void;
  notify: Notify;
};

const CHECKLIST_KEY = "conversa-smart:pilot-checklist:v1";

const CHECKLIST = [
  ["vm", "VM Ubuntu disponible", "Hyper-V, SSH, 8 GB RAM y disco persistente."],
  ["aws", "Control AWS operativo", "API, Lambda, DynamoDB y token del bridge responden."],
  ["snapshot", "Punto de recuperación creado", "Checkpoint base antes de vincular WhatsApp."],
  ["power", "Continuidad del host", "Sin suspensión, tapa configurada y encendido tras corte."],
  ["sim", "Caso piloto creado", "Conversación de prueba guardada y revisada en AWS."],
  ["roles", "Roles definidos", "Administración, operaciones, supervisión, técnico y auditoría."],
  ["catalog", "Clientes e instalaciones priorizados", "Catálogo inicial de la organización activa."],
  ["twilio", "Canal Twilio clasificado", "Uso oficial individual separado del bridge de grupos."],
  ["simcard", "SIM corporativa disponible", "Número móvil dedicado con WhatsApp Business."],
  ["qr", "QR vinculado", "Dispositivo asociado desde WhatsApp Business."],
  ["group", "Grupo canario habilitado", "Aviso de privacidad, lista blanca y retención."],
  ["recovery", "Prueba de recuperación aprobada", "Reinicio de Windows, VM y bridge sin pérdida."],
] as const;

const ROLE_MATRIX = [
  ["Mario Pérez-García", "Administrador", "Configuración, organizaciones, canales y decisiones"],
  ["Enrique Pérez-García", "Gerencia / Operaciones", "Seguimiento de pilotos, reuniones y cierres"],
  ["Esteban", "Operaciones", "Orden, compromisos, campañas y coordinación"],
  ["Marcelo Zavala", "Técnico", "Diagnóstico CCTV, visita y evidencia técnica"],
  ["Supervisores", "Supervisor", "Casos por instalación y cumplimiento operativo"],
  ["Cliente o revisor", "Auditor", "Lectura acotada, sin respuesta ni edición"],
] as const;

const CUSTOMER_CATALOG = [
  ["Carozzi", "Bresler · Chena · Concepción", "CCTV, alarmas, accesos y mantenimiento"],
  ["Cementos Melón", "Ñilhue", "Guardias, rondas, vehículo y reportes"],
  ["Lo Valledor", "P10", "Barreras, accesos e incidentes"],
  ["Walmart", "Por definir", "Seguridad física y tecnología"],
  ["Cimenta", "Por definir", "Seguridad de obras e instalaciones"],
  ["Liga contra la Epilepsia", "Por definir", "Operación y seguimiento de seguridad"],
] as const;

const REPORT_TEMPLATES = [
  ["Reporte diario", "Solicitudes, incidentes, responsables, vencidos y evidencia del día."],
  ["Resumen semanal de gerencia", "Actividad por instalación, pendientes críticos y tiempos de respuesta."],
  ["Informe de visita técnica", "Antecedentes, diagnóstico, trabajos, evidencia y próximas acciones."],
  ["Reporte para cliente", "Resumen ejecutivo, solicitudes atendidas, compromisos, plazos y respaldo."],
] as const;

export function PilotCenter({
  session,
  organizationName,
  isAdministrator,
  remote,
  onCreateScenario,
  onNavigate,
  notify,
}: PilotCenterProps) {
  const [connections, setConnections] = useState<LocalBridgeConnection[]>([]);
  const [bridgeLoading, setBridgeLoading] = useState(false);
  const [profile, setProfile] = useState<PilotProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [scenarioBusy, setScenarioBusy] = useState("");
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => readChecklist());

  useEffect(() => {
    if (!session || !isAdministrator) return;
    let active = true;
    setBridgeLoading(true);
    void listLocalBridgeConnections(session)
      .then((items) => active && setConnections(items))
      .catch(() => active && setConnections([]))
      .finally(() => active && setBridgeLoading(false));
    return () => { active = false; };
  }, [session, isAdministrator]);

  useEffect(() => {
    if (!session || !remote) return;
    let active = true;
    setProfileLoading(true);
    void getRemotePilotProfile(session)
      .then((item) => {
        if (!active) return;
        setProfile(item);
        setChecklist(item.checklist);
        window.localStorage.setItem(CHECKLIST_KEY, JSON.stringify(item.checklist));
      })
      .catch((cause) => {
        if (active) notify("warning", `No se pudo cargar el perfil compartido del piloto: ${cause instanceof Error ? cause.message : "error desconocido"}.`);
      })
      .finally(() => active && setProfileLoading(false));
    return () => { active = false; };
  }, [session, remote]);

  const primaryBridge = connections[0];
  const roleMatrix = profile?.roles ?? ROLE_MATRIX.map(([person, role, scope]) => ({ person, role, scope }));
  const customerCatalog = profile?.customers ?? CUSTOMER_CATALOG.map(([customer, sites, focus]) => ({ customer, sites, focus }));
  const reportTemplates = profile?.reportTemplates ?? REPORT_TEMPLATES.map(([title, description]) => ({ title, description }));
  const completed = useMemo(
    () => CHECKLIST.filter(([id]) => checklist[id]).length,
    [checklist],
  );
  const percent = Math.round((completed / CHECKLIST.length) * 100);

  async function createScenario(id: string) {
    if (remote) {
      notify("warning", "Los casos ficticios sólo pueden crearse en el modo demostración local y nunca se guardan en AWS.");
      return;
    }
    const scenario = pilotScenarios.find((item) => item.id === id);
    if (!scenario) return;
    setScenarioBusy(id);
    try {
      await onCreateScenario(scenario.build());
      setChecklistValue("sim", true);
      notify("success", `${scenario.title}: caso piloto creado y listo para revisar.`);
    } catch (cause) {
      notify("danger", `No se pudo crear el caso piloto: ${cause instanceof Error ? cause.message : "error desconocido"}`);
    } finally {
      setScenarioBusy("");
    }
  }

  async function createAllScenarios() {
    if (remote) {
      notify("warning", "Los casos ficticios sólo pueden crearse en el modo demostración local y nunca se guardan en AWS.");
      return;
    }
    setScenarioBusy("all");
    try {
      for (const scenario of pilotScenarios) await onCreateScenario(scenario.build());
      setChecklistValue("sim", true);
      notify("success", "Los tres casos controlados quedaron creados para revisar operación, venta y servicio técnico.");
    } catch (cause) {
      notify("danger", `No se pudieron crear todos los casos: ${cause instanceof Error ? cause.message : "error desconocido"}`);
    } finally {
      setScenarioBusy("");
    }
  }

  function setChecklistValue(id: string, value: boolean) {
    setChecklist((current) => {
      const next = { ...current, [id]: value };
      window.localStorage.setItem(CHECKLIST_KEY, JSON.stringify(next));
      return next;
    });
    if (session && remote) {
      setProfileLoading(true);
      void updateRemotePilotProfile({ checklist: { [id]: value } }, session)
        .then((item) => {
          setProfile(item);
          setChecklist(item.checklist);
          window.localStorage.setItem(CHECKLIST_KEY, JSON.stringify(item.checklist));
        })
        .catch((cause) => notify("warning", `El cambio quedó local, pero AWS no pudo guardarlo: ${cause instanceof Error ? cause.message : "error desconocido"}.`))
        .finally(() => setProfileLoading(false));
    }
  }

  return (
    <div className="pilot-page standard-page">
      <section className="pilot-hero">
        <div>
          <p className="section-kicker">CENTRO DE PILOTO · {organizationName?.toUpperCase() ?? "CONVERSA SMART"}</p>
          <h1>Deja la operación lista antes de conectar el chip.</h1>
          <p>
            Prueba conversaciones, compromisos, visitas, reportes, roles y continuidad sin depender todavía del QR de WhatsApp.
          </p>
          <div className="hero-badges">
            <span><Icon name="check" size={16} /> Sin Meta ni SIM</span>
            <span><Icon name="check" size={16} /> AWS verificable</span>
            <span><Icon name="check" size={16} /> Casos ficticios controlados</span>
          </div>
        </div>
        <aside className="pilot-progress-card">
          <div><span>AVANCE DEL PILOTO</span><strong>{percent}%</strong></div>
          <div className="pilot-progress"><i style={{ width: `${percent}%` }} /></div>
          <p>{completed} de {CHECKLIST.length} controles completados.</p>
          <button className="button soft compact" onClick={() => document.getElementById("pilot-checklist")?.scrollIntoView({ behavior: "smooth" })} type="button">
            Ver checklist <Icon name="arrow" size={15} />
          </button>
        </aside>
      </section>

      <section className="pilot-status-grid">
        <StatusCard
          icon="visit"
          title="VM Crelander"
          state="Lista"
          tone="success"
          detail="Ubuntu 24.04 · Hyper-V · bridge instalado"
        />
        <StatusCard
          icon="settings"
          title="Control AWS"
          state={primaryBridge?.lastHeartbeatAt ? "Con heartbeat" : bridgeLoading ? "Revisando" : "Pendiente"}
          tone={primaryBridge?.lastHeartbeatAt ? "success" : "warning"}
          detail={primaryBridge?.lastHeartbeatAt ? `Último reporte ${formatRelative(primaryBridge.lastHeartbeatAt)}` : "Se valida sin exponer el token"}
        />
        <StatusCard
          icon="whatsapp"
          title="Bridge de grupos"
          state={primaryBridge ? stateLabel(primaryBridge.state) : "Sin lectura"}
          tone={primaryBridge?.state === "ready" ? "success" : primaryBridge ? "warning" : "neutral"}
          detail={primaryBridge?.label ?? `Conversa ${organizationName ?? "Smart"} · Grupos`}
        />
        <StatusCard
          icon="clock"
          title="Número Twilio"
          state="Canal oficial futuro"
          tone="neutral"
          detail="Conversaciones individuales; no reemplaza la SIM para grupos"
        />
      </section>

      <section className="pilot-section">
        <div className="pilot-section-heading">
          <div><p className="section-kicker">PRUEBA AHORA</p><h2>Crea un caso operacional completo</h2><p>{remote ? "Los casos ficticios se muestran como referencia, pero sólo pueden crearse en el modo demo local y nunca se copian a AWS." : "El caso queda únicamente en la demostración local y permite generar compromisos, visita y reporte."}</p></div>
          <div className="pilot-heading-actions">
            <button className="button secondary compact" disabled={remote || Boolean(scenarioBusy)} onClick={() => void createAllScenarios()} type="button"><Icon name="plus" size={16} /> {remote ? "Sólo en demo local" : scenarioBusy === "all" ? "Creando casos…" : "Crear 3 casos controlados"}</button>
            <button className="button secondary compact" onClick={() => onNavigate("import")} type="button"><Icon name="import" size={16} /> Importar un chat real</button>
          </div>
        </div>
        <div className="pilot-scenario-grid">
          {pilotScenarios.map((scenario) => (
            <article className="pilot-scenario-card" key={scenario.id}>
              <p className="section-kicker">{scenario.eyebrow}</p>
              <h3>{scenario.title}</h3>
              <p>{scenario.description}</p>
              <div className="pilot-outcome"><Icon name="check" size={16} /><span>{scenario.outcome}</span></div>
              <button className="button primary" disabled={remote || Boolean(scenarioBusy)} onClick={() => void createScenario(scenario.id)} type="button">
                {remote ? "Disponible en demo local" : scenarioBusy === scenario.id ? "Creando caso…" : "Crear caso piloto"}<Icon name="arrow" size={16} />
              </button>
            </article>
          ))}
        </div>
      </section>

      <PilotSampleChats />

      <section className="pilot-two-column">
        <article className="card-panel pilot-table-card">
          <div className="panel-title-row"><div><p className="section-kicker">GOBERNANZA</p><h2>Matriz inicial de roles</h2></div><Icon name="team" size={22} /></div>
          <div className="pilot-table">
            {roleMatrix.map(({ person, role, scope }) => <div key={person}><strong>{person}</strong><span>{role}</span><small>{scope}</small></div>)}
          </div>
          <p className="pilot-footnote">Las invitaciones requieren confirmar los correos corporativos antes de enviarlas.</p>
        </article>

        <article className="card-panel pilot-table-card">
          <div className="panel-title-row"><div><p className="section-kicker">ESTRUCTURA</p><h2>Clientes e instalaciones</h2></div><Icon name="visit" size={22} /></div>
          <div className="pilot-table">
            {customerCatalog.map(({ customer, sites, focus }) => <div key={customer}><strong>{customer}</strong><span>{sites}</span><small>{focus}</small></div>)}
          </div>
          <p className="pilot-footnote">El catálogo definitivo debe validarse con operaciones antes de habilitar grupos reales.</p>
        </article>
      </section>

      <section className="pilot-section">
        <div className="pilot-section-heading"><div><p className="section-kicker">SALIDAS</p><h2>Reportes preparados para el piloto</h2><p>Cada documento mantiene vínculo con mensajes, responsables y plazos.</p></div><button className="button soft compact" onClick={() => onNavigate("reports")} type="button">Abrir reportes</button></div>
        <div className="pilot-report-grid">
          {reportTemplates.map(({ title, description }) => <article key={title}><span><Icon name="document" size={19} /></span><div><strong>{title}</strong><p>{description}</p></div></article>)}
        </div>
      </section>

      <section className="pilot-two-column">
        <article className="card-panel pilot-channel-card">
          <p className="section-kicker">TWILIO · OFICIAL</p>
          <h2>Reserva el número virtual para atención individual</h2>
          <p>La arquitectura prevista separa el canal oficial de Twilio del número móvil que participará en grupos mediante Baileys.</p>
          <ol>
            <li>Confirmar si el número aparece como WhatsApp Sender aprobado.</li>
            <li>Crear conexión Twilio en AWS sin exponer Auth Token en el navegador.</li>
            <li>Validar firma del webhook y normalizar mensajes hacia la ingesta existente.</li>
            <li>Habilitar respuesta sólo cuando la ventana y las plantillas lo permitan.</li>
          </ol>
          {isAdministrator ? <button className="button secondary" onClick={() => onNavigate("channels")} type="button"><Icon name="settings" size={16} /> Abrir canales</button> : null}
        </article>

        <article className="card-panel pilot-channel-card">
          <p className="section-kicker">BAILEYS · GRUPOS</p>
          <h2>La SIM móvil se usará sólo donde agrega valor</h2>
          <p>Hasta tener el chip, actualiza una sola vez y deja el bridge en modo <strong>paused</strong>: mantiene heartbeat y control AWS sin generar códigos QR.</p>
          <div className="pilot-code-block">
            <code>cd ~/conversa-smart && git pull --ff-only</code>
            <code>sudo bash bridge/no-sim-ready.sh</code>
          </div>
          <a className="button soft" href="/local-bridge.html" target="_blank" rel="noreferrer"><Icon name="external" size={16} /> Abrir control Baileys</a>
        </article>
      </section>

      <section className="pilot-section" id="pilot-checklist">
        <div className="pilot-section-heading"><div><p className="section-kicker">PUERTA DE SALIDA</p><h2>Checklist para autorizar el piloto real</h2><p>{remote ? "El avance se comparte por organización y queda auditado en AWS." : "El avance se conserva localmente en esta demostración."}</p></div><span className="count-badge">{profileLoading ? "Guardando…" : `${completed}/${CHECKLIST.length}`}</span></div>
        <div className="pilot-checklist">
          {CHECKLIST.map(([id, title, description], index) => (
            <label className={checklist[id] ? "complete" : ""} key={id}>
              <input type="checkbox" checked={Boolean(checklist[id])} onChange={(event) => setChecklistValue(id, event.target.checked)} />
              <span>{checklist[id] ? <Icon name="check" size={18} /> : index + 1}</span>
              <div><strong>{title}</strong><p>{description}</p></div>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatusCard({
  icon,
  title,
  state,
  detail,
  tone,
}: {
  icon: "visit" | "settings" | "whatsapp" | "clock";
  title: string;
  state: string;
  detail: string;
  tone: "success" | "warning" | "neutral";
}) {
  return <article className={`pilot-status-card ${tone}`}><span><Icon name={icon} size={20} /></span><div><small>{title}</small><strong>{state}</strong><p>{detail}</p></div></article>;
}

function stateLabel(state: LocalBridgeConnection["state"]) {
  const labels: Record<LocalBridgeConnection["state"], string> = {
    provisioning: "Preparando",
    awaiting_qr: "Esperando QR",
    authenticated: "Autenticado",
    ready: "Conectado",
    degraded: "Degradado",
    offline: "Desconectado",
    error: "Con error",
    paused: "En espera",
    revoked: "Revocado",
  };
  return labels[state];
}

function formatRelative(value: string) {
  const elapsed = Date.now() - Date.parse(value);
  if (!Number.isFinite(elapsed)) return "registrado";
  const minutes = Math.max(0, Math.round(elapsed / 60_000));
  if (minutes < 1) return "hace menos de un minuto";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  return `hace ${hours} h`;
}

function readChecklist() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(CHECKLIST_KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}
